import { attach, createEffect, createEvent, createStore, sample } from 'effector'

import {
    type ClientRuntime,
    type GameConnectConfig,
    type GameConnectRequest,
    type GameConnectResult,
    type PortalAPIError,
    parseGameConfigResponse,
} from '@portal/portal-api'

import { portalAPI, refreshTokenIfNeeded } from '@/shared/api'

import { $gameClient } from './client'
import { $clientRuntime } from './runtime'
import type { GameConnectAttempt } from './types'

export const $gameConnectResult = createStore<GameConnectResult | null>(null)
export const $gameConnectConfig = $gameConnectResult.map((response) =>
    response?.type === 'config' ? response.config : null
)

export const $gameConnected = $gameConnectConfig.map((result) => result !== null)

const gameConnectRequestFx = createEffect<GameConnectRequest, GameConnectConfig, PortalAPIError>(async (params) => {
    try {
        return await portalAPI.gameConnect(params)
    } catch (error) {
        if (!(await refreshTokenIfNeeded(error, portalAPI.refreshToken))) {
            throw error
        }

        return await portalAPI.gameConnect(params)
    }
})

export const gameConnectFx = attach({
    effect: gameConnectRequestFx,
    source: $clientRuntime,
    mapParams: (_attempt: GameConnectAttempt, clientRuntime: ClientRuntime) => ({ client_name: clientRuntime }),
})

export const gameConnectAttemptFinished = gameConnectFx.finally.map(({ params: attempt, ...response }) => ({
    attempt,
    result: parseGameConfigResponse(response),
}))

// A transport reconnect failure stays inside the mounted game client's retry loop.
export const gameConnectFinished = gameConnectAttemptFinished.filterMap(({ attempt, result }) => {
    if (attempt.phase === 'reconnect' && result.type === 'error' && result.error.code === 'transport_error') {
        return
    }

    return { attempt, result }
})

sample({
    clock: gameConnectFinished,
    fn: ({ result }) => result,
    target: $gameConnectResult,
})

export const resetPlayPageState = createEvent<void>()

sample({
    clock: resetPlayPageState,
    fn: () => null,
    target: [$gameConnectResult, $gameClient],
})
